import { useMemo } from 'react';
import type { HighlightKey } from '../../content/types';
import { forceComponents } from '../../simulation/model';
import type { ExperimentConfig, Sample } from '../../simulation/types';
import { GUIDE_TOP } from '../../simulation/simulation';

interface Props {
  config: ExperimentConfig;
  sample: Sample | null;
  highlight: HighlightKey | null;
  reducedMotion: boolean;
  playing: boolean;
  /** Highest altitude reached so far; the view grows (in 2 m steps) so the vehicle stays visible. */
  maxAltitude?: number;
}

const H = 360;
const W = 320;
const GROUND = H - 24;
const TOP = 16;
const PX_PER_N = 4.5;
const MAX_ARROW = 110;

function sceneTop(c: ExperimentConfig): number {
  const refs = [c.reference.initial, ...c.reference.steps.map((s) => s.value), c.initial.z, c.z0];
  return Math.min(GUIDE_TOP, Math.max(4, Math.ceil(Math.max(...refs) + 1.5)));
}

const fmt = (x: number, d = 2) => (Math.abs(x) < 0.005 ? '0.00' : x.toFixed(d));

export function Scene({ config, sample, highlight, reducedMotion, playing, maxAltitude = 0 }: Props) {
  const base = useMemo(() => sceneTop(config), [config]);
  const zTop = Math.min(GUIDE_TOP, Math.max(base, Math.ceil((maxAltitude + 0.8) / 2) * 2));
  const zToY = (z: number) => GROUND - (z / zTop) * (GROUND - TOP);
  const p = config.plant;
  const s = sample;
  const z = s?.z ?? config.initial.z;
  const v = s?.v ?? config.initial.v;
  const T = s?.T ?? 0;
  const d = s?.d ?? 0;
  const r = s?.r ?? config.reference.initial;
  const f = forceComponents(p, z, v, T, d);
  const vy = Math.max(TOP + 6, Math.min(GROUND - 6, zToY(z)));
  const cx = 150;
  const tickStep = zTop > 6 ? 1 : 0.5;
  const ticks: number[] = [];
  for (let t = 0; t <= zTop + 1e-9; t += tickStep) ticks.push(+t.toFixed(2));
  const hasSpring = p.k > 0;
  const rotorSpeed = T > 0 ? Math.max(0.12, 1.2 - (T / p.Tmax) * 1.05) : 0;
  const hl = (key: HighlightKey) => (highlight === key ? ' is-hl' : highlight?.startsWith('force:') ? ' is-dim' : '');

  const arrows: { key: HighlightKey; label: string; value: number; x: number; cls: string }[] = [
    { key: 'force:thrust', label: 'thrust', value: f.thrust, x: cx - 30, cls: 'f-thrust' },
    { key: 'force:gravity', label: 'weight', value: f.gravity, x: cx - 10, cls: 'f-gravity' },
    { key: 'force:spring', label: 'spring', value: f.spring, x: cx + 10, cls: 'f-spring' },
    { key: 'force:damping', label: hasSpring ? 'damping' : 'drag', value: f.damping, x: cx + 30, cls: 'f-damping' },
    { key: 'force:disturbance', label: 'load', value: f.disturbance, x: cx + 50, cls: 'f-dist' },
  ];

  const springPath = useMemo(() => {
    const x0 = 60;
    const yTop = vy;
    const yBot = GROUND;
    const n = 12;
    let dd = `M${x0},${yBot} L${x0},${yBot - 8}`;
    const span = yBot - 8 - (yTop + 8);
    for (let i = 1; i <= n; i++) {
      const yy = yBot - 8 - (span * i) / n;
      dd += ` L${x0 + (i % 2 ? 8 : -8)},${yy + span / n / 2} L${x0},${yy}`;
    }
    dd += ` L${x0},${yTop} L${cx - 26},${yTop}`;
    return dd;
  }, [vy]);

  const desc =
    `Vehicle at altitude ${fmt(z)} m moving ${v > 0.005 ? 'up' : v < -0.005 ? 'down' : 'not at all'} at ${fmt(Math.abs(v))} m/s; target ${fmt(r)} m. ` +
    `Forces: thrust ${fmt(f.thrust)} N up, weight ${fmt(-f.gravity)} N down` +
    (hasSpring ? `, spring ${fmt(f.spring)} N` : '') +
    `, damping ${fmt(f.damping)} N` +
    (d !== 0 ? `, load ${fmt(d)} N` : '') +
    `; net ${fmt(f.net)} N.`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="scene" role="img" aria-label={desc}>
      <defs>
        <marker id="arrowhead" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="context-stroke" />
        </marker>
      </defs>
      {/* ground */}
      <rect x="0" y={GROUND} width={W} height={H - GROUND} className="ground" />
      <text x="8" y={H - 8} className="scene-label">
        ground (z = 0)
      </text>
      {/* guide rail and ruler */}
      <line x1={cx} x2={cx} y1={TOP} y2={GROUND} className="rail" />
      {ticks.map((t) => (
        <g key={t}>
          <line x1={W - 40} x2={W - (Number.isInteger(t) ? 26 : 32)} y1={zToY(t)} y2={zToY(t)} className="ruler-tick" />
          {Number.isInteger(t) && (
            <text x={W - 22} y={zToY(t) + 3} className="scene-label">
              {t} m
            </text>
          )}
        </g>
      ))}
      <line x1={W - 40} x2={W - 40} y1={TOP} y2={GROUND} className="ruler-tick" />
      {/* target marker */}
      <g className={`target${highlight === 'signal:r' || highlight === 'scene:target' ? ' is-hl' : ''}`}>
        <line x1={cx - 70} x2={W - 40} y1={zToY(r)} y2={zToY(r)} />
        <text x={cx + 72} y={zToY(r) - 4} className="scene-label">
          target r = {fmt(r)} m
        </text>
      </g>
      {/* spring rest marker and spring/damper */}
      {hasSpring && (
        <g className={`spring${highlight === 'scene:spring' || highlight === 'force:spring' ? ' is-hl' : ''}`}>
          <line x1={40} x2={80} y1={zToY(p.zs)} y2={zToY(p.zs)} className="spring-rest" />
          <text x={8} y={zToY(p.zs) - 4} className="scene-label">
            z_s
          </text>
          <path d={springPath} className="spring-coil" />
          {/* dashpot */}
          <g className={`dashpot${hl('force:damping')}`}>
            <line x1={92} x2={92} y1={GROUND} y2={(GROUND + vy) / 2 + 10} />
            <rect x={85} y={(GROUND + vy) / 2 - 10} width={14} height={20} />
            <line x1={92} x2={92} y1={(GROUND + vy) / 2} y2={vy} />
            <line x1={92} x2={cx - 26} y1={vy} y2={vy} />
          </g>
          <text x={8} y={GROUND - 6} className="scene-label">
            teaching spring k = {p.k} N/m
          </text>
        </g>
      )}
      {/* vehicle */}
      <g transform={`translate(${cx},${vy})`} className="vehicle">
        <rect x={-26} y={-5} width={52} height={10} rx={3} className="body" />
        <line x1={-40} x2={40} y1={-8} y2={-8} className="arm" />
        {[-40, 40].map((rx) => (
          <g key={rx} transform={`translate(${rx},-11)`}>
            <line x1={0} x2={0} y1={0} y2={3} className="arm" />
            <ellipse
              rx={14}
              ry={2.5}
              className={`rotor${!reducedMotion && playing && T > 0 ? ' spinning' : ''}`}
              style={{ animationDuration: `${rotorSpeed}s` }}
            />
          </g>
        ))}
      </g>
      {/* force arrows */}
      {arrows.map((a) => {
        if (Math.abs(a.value) < 0.02 && a.key !== 'force:thrust' && a.key !== 'force:gravity') return null;
        const len = Math.min(MAX_ARROW, Math.abs(a.value) * PX_PER_N);
        const up = a.value > 0;
        const y0 = vy + (up ? -6 : 6);
        const y1 = y0 + (up ? -len : len);
        const clipped = Math.abs(a.value) * PX_PER_N > MAX_ARROW;
        return (
          <g key={a.key} className={`force ${a.cls}${hl(a.key)}`}>
            {len > 2 && <line x1={a.x} x2={a.x} y1={y0} y2={y1} markerEnd="url(#arrowhead)" />}
            {clipped && <line x1={a.x - 4} x2={a.x + 4} y1={(y0 + y1) / 2 - 2} y2={(y0 + y1) / 2 + 2} className="break" />}
          </g>
        );
      })}
      {/* force value labels in a legend box (not colour-dependent) */}
      <g className="force-legend" transform={`translate(8, ${TOP + 4})`}>
        {arrows
          .filter((a) => a.key !== 'force:spring' || hasSpring)
          .map((a, i) => (
            <text key={a.key} y={i * 13} className={`scene-label ${a.cls}${hl(a.key)}`}>
              {a.label}: {fmt(a.value)} N {a.value > 0.005 ? '↑' : a.value < -0.005 ? '↓' : ''}
            </text>
          ))}
        <text y={arrows.length * 13 + 2} className={`scene-label strong${highlight === 'force:net' ? ' is-hl' : ''}`}>
          net: {fmt(f.net)} N {f.net > 0.005 ? '↑' : f.net < -0.005 ? '↓' : ''}
        </text>
      </g>
      {/* velocity indicator */}
      <g className={`velocity${highlight === 'signal:v' ? ' is-hl' : ''}`} transform={`translate(${cx + 88}, ${vy})`}>
        {Math.abs(v) > 0.01 && (
          <line x1={0} x2={0} y1={0} y2={-Math.max(-50, Math.min(50, v * 30))} markerEnd="url(#arrowhead)" />
        )}
        <text x={6} y={18} className="scene-label">
          v = {fmt(v)} m/s
        </text>
      </g>
    </svg>
  );
}
